import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaCalendarAlt,
  FaCamera,
  FaCheck,
  FaEllipsisH,
  FaHandshake,
  FaLightbulb,
  FaPen,
  FaSpinner,
} from "react-icons/fa";
import { toast } from "react-toastify";

import bannerSbf from "../../assets/credenciamento2026/banner2026-credenciamento.png";
import ilustBaiana from "../../assets/credenciamento2026/baiana-2026.png";
import footerCred2026 from "../../assets/credenciamento2026/footer-2026.png";
import "./credenciamento2026.css";
import "../credenciamentoConteudos/credenciamentoConteudos.css";
import { submitCredenciamento2026 } from "./credenciamento2026Api";
import { listarConteudosAgenda } from "../credenciamentoConteudos/credenciamentoConteudosApi";
import {
  encontrarConteudoPorId,
  idsConteudosRegulares,
} from "../credenciamentoConteudos/conteudosAgendaUtils";
import PassoConteudos from "../credenciamentoConteudos/PassoConteudos";
import PassoMatchMeet from "../credenciamentoConteudos/PassoMatchMeet";
import api from "../../services/axios";
import {
  ESTADOS_BR,
  REGIOES_BR,
  regiaoPorEstado,
  isRegiaoValidaParaEstado,
} from "../../constants/estadosRegioes";

const SEGMENTOS = [
  ["clinica", "Clínica"],
  ["farmaceutica", "Farmacêutica"],
  ["homecare", "Home care"],
  ["hospital", "Hospital"],
  ["laboratorio", "Laboratório"],
  ["operadora", "Operadora"],
  ["outros", "Outros"],
];

const PROCURA = [
  ["consultoria", "Consultoria"],
  ["contrucao_civil", "Construção civil"],
  ["consumiveis", "Consumíveis"],
  ["equipamentos", "Equipamentos"],
  ["facilities", "Facilities"],
  ["homecare", "Home care"],
  ["servicos", "Serviços"],
  ["tecnologia", "Tecnologia"],
  ["outros", "Outros"],
];

const MAX_BIO_FOTO_BYTES = 5 * 1024 * 1024;

const FATURAMENTO = [
  ["Abaixo de 1M", "Abaixo de 1M"],
  ["Entre 1M e 300M", "Entre 1M e 300M"],
  ["Entre 300M e 500M", "Entre 300M e 500M"],
  ["Entre 500M e 1B", "Entre 500M e 1B"],
  ["Entre 1B e 5B", "Entre 1B e 5B"],
  ["Superior a 5B", "Superior a 5B"],
];

const ATIVIDADES_INTERESSE = [
  ["gastronomia", "Gastronomia"],
  ["esportes", "Esportes"],
  ["cinema", "Cinema"],
  ["viagens", "Viagens"],
  ["leitura", "Leitura"],
  ["outros", "Outros"],
];

function onlyDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

function formatCpf(v) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function formatPhone(v) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());
}

async function usuarioJaCadastradoPorEmail(email) {
  const trimmed = String(email || "").trim();
  if (!trimmed) return false;
  try {
    const resp = await api.post("/usuarios-verifica-email", { email: trimmed }, {
      skipAuth: true,
    });
    return Boolean(resp.data?.dados);
  } catch {
    return false;
  }
}

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function isValidDateBr(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || "").trim());
  if (!m) return false;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(year, month - 1, day);
  return (
    dt.getFullYear() === year &&
    dt.getMonth() === month - 1 &&
    dt.getDate() === day
  );
}

const initialForm = () => ({
  nomeCracha: "",
  email: "",
  cpf: "",
  dataNascimento: "",
  sexo: "",
  telComercial: "",
  celular: "",
  estado: "",
  regiao: "",
  nomeInstituicao: "",
  cargo: "",
  segmento: "",
  segmentoOutros: "",
  secretaria: "",
  telSecretaria: "",
  emailSecretaria: "",
  procura: "",
  outrosProcura: "",
  faturamento: "",
  leitos: "",
  vidas: "",
  prioridade: "",
  transformacao: "",
  sugestaoEmpresa: "",
  acaoEspecial: "",
  usoImagem: false,
  bioCurta: "",
  atividadeInteresse: "",
  atividadeInteresseOutros: "",
  aceiteRegulamento: false,
});

const STEPS_META = [
  { key: 0, label: "Identificação", Icon: FaLightbulb },
  { key: 1, label: "Institucional", Icon: FaEllipsisH },
  { key: 2, label: "Bio", Icon: FaCamera },
  { key: 3, label: "Conteúdos", Icon: FaCalendarAlt },
  { key: 4, label: "Match & Meet", Icon: FaHandshake },
  { key: 5, label: "Revisão", Icon: FaCheck },
];

const STEP_CONTEUDOS = 3;
const STEP_MATCH_MEET = 4;
const STEP_REVISAO = 5;

function formatarHorarioConteudo(item) {
  const raw = String(item?.dt_hora_inicio || "").trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(raw);
  const dataHora = match
    ? `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`
    : raw.slice(0, 16).replace("T", " ");
  return dataHora + (item?.local ? ` · ${item.local}` : "");
}

const REVIEW_MODAL_TITLES = {
  cracha_email: "Editar crachá e e-mail",
  estado_regiao: "Editar estado e região",
  instituicao: "Editar instituição e cargo",
  segmento: "Editar segmento",
  procura: "Editar procura no fórum",
  faturamento: "Editar faturamento",
  bio: "Editar mini bio",
  atividade_interesse: "Editar atividade de interesse",
  foto: "Editar foto de perfil",
  uso_imagem: "Editar autorização de uso de imagem",
};

function Credenciamento2026() {
  const uid = useId();
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [protocolo, setProtocolo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [form, setForm] = useState(initialForm);
  const [bioFoto, setBioFoto] = useState(null);
  const [bioFotoPreview, setBioFotoPreview] = useState("");
  const bioFileInputRef = useRef(null);
  const [reviewEdit, setReviewEdit] = useState(null);
  const [editDraft, setEditDraft] = useState(() => initialForm());
  const [modalBioFoto, setModalBioFoto] = useState(null);
  const [modalFotoPreview, setModalFotoPreview] = useState("");
  const modalFotoInputRef = useRef(null);
  const [emailLoginVerificando, setEmailLoginVerificando] = useState(false);
  const [emailLoginJaExiste, setEmailLoginJaExiste] = useState(false);
  const [emailModalVerificando, setEmailModalVerificando] = useState(false);
  const [emailModalJaExiste, setEmailModalJaExiste] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [empresasMatchMeet, setEmpresasMatchMeet] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [dias, setDias] = useState([]);
  const [carregandoAgenda, setCarregandoAgenda] = useState(false);
  const [agendaCarregada, setAgendaCarregada] = useState(false);

  useEffect(() => {
    if (!bioFoto) {
      setBioFotoPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(bioFoto);
    setBioFotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [bioFoto]);

  useEffect(() => {
    if (step < STEP_CONTEUDOS || agendaCarregada) return undefined;
    let cancelado = false;
    (async () => {
      setCarregandoAgenda(true);
      try {
        const data = await listarConteudosAgenda();
        if (cancelado) return;
        if (!data?.ok) {
          toast.error(data?.mensagem || "Não foi possível carregar os conteúdos.");
          setDias([]);
          setCatalogo([]);
          return;
        }
        setDias(data?.dados?.dias || []);
        setCatalogo(data?.dados?.catalogo || []);
        setAgendaCarregada(true);
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        toast.error("Erro ao carregar a agenda de conteúdos.");
        setDias([]);
        setCatalogo([]);
      } finally {
        if (!cancelado) setCarregandoAgenda(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [step, agendaCarregada]);

  const conteudosSelecionados = useMemo(
    () =>
      idsConteudosRegulares(selectedIds, catalogo)
        .map((id) => encontrarConteudoPorId(id, catalogo))
        .filter(Boolean),
    [selectedIds, catalogo],
  );

  const clearBioFoto = useCallback(() => {
    setBioFoto(null);
    if (bioFileInputRef.current) bioFileInputRef.current.value = "";
    markTouched(["bioFoto"]);
  }, []);

  const handleBioFotoChange = useCallback((e) => {
    const input = e.target;
    const f = input.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Envie apenas arquivo de imagem (JPG, PNG, WebP ou GIF).");
      input.value = "";
      markTouched(["bioFoto"]);
      return;
    }
    if (f.size > MAX_BIO_FOTO_BYTES) {
      toast.error("Imagem muito grande. Tamanho máximo: 5 MB.");
      input.value = "";
      markTouched(["bioFoto"]);
      return;
    }
    setBioFoto(f);
    markTouched(["bioFoto"]);
  }, []);

  const update = useCallback((patch) => {
    setForm((f) => ({ ...f, ...patch }));
  }, []);

  const openReviewEdit = useCallback(
    (key) => {
      setEditDraft({ ...form });
      if (key === "foto") setModalBioFoto(bioFoto);
      if (key === "cracha_email") {
        setEmailModalJaExiste(false);
        setEmailModalVerificando(false);
      }
      setReviewEdit(key);
    },
    [form, bioFoto]
  );

  const closeReviewEdit = useCallback(() => {
    setReviewEdit(null);
  }, []);

  const saveReviewEdit = useCallback(async () => {
    if (reviewEdit === "foto") {
      if (!modalBioFoto) {
        toast.error("Envie uma foto para o perfil.");
        return;
      }
      setBioFoto(modalBioFoto);
    } else if (reviewEdit === "cracha_email") {
      if (!String(editDraft.nomeCracha || "").trim()) {
        toast.error("Informe o nome para o crachá.");
        return;
      }
      const em = String(editDraft.email || "").trim();
      if (!isValidEmail(em)) {
        toast.error("Informe um e-mail válido.");
        return;
      }
      setEmailModalVerificando(true);
      try {
        const existe = await usuarioJaCadastradoPorEmail(em);
        setEmailModalJaExiste(existe);
        if (existe) {
          toast.error("Este e-mail já possui cadastro.");
          return;
        }
      } finally {
        setEmailModalVerificando(false);
      }
      update(editDraft);
    } else if (reviewEdit === "estado_regiao") {
      if (!String(editDraft.estado || "").trim()) {
        toast.error("Selecione o estado.");
        return;
      }
      if (
        !String(editDraft.regiao || "").trim() ||
        !isRegiaoValidaParaEstado(editDraft.estado, editDraft.regiao)
      ) {
        toast.error("Selecione um estado válido para preencher a região.");
        return;
      }
      update({
        estado: editDraft.estado,
        regiao: editDraft.regiao,
      });
    } else if (reviewEdit === "instituicao") {
      if (!String(editDraft.nomeInstituicao || "").trim()) {
        toast.error("Informe o nome da instituição.");
        return;
      }
      if (!String(editDraft.cargo || "").trim()) {
        toast.error("Informe o cargo.");
        return;
      }
      update({
        nomeInstituicao: editDraft.nomeInstituicao,
        cargo: editDraft.cargo,
      });
    } else if (reviewEdit === "segmento") {
      if (!editDraft.segmento) {
        toast.error("Selecione o segmento.");
        return;
      }
      if (
        editDraft.segmento === "outros" &&
        !String(editDraft.segmentoOutros || "").trim()
      ) {
        toast.error("Descreva o segmento.");
        return;
      }
      update({
        segmento: editDraft.segmento,
        segmentoOutros: editDraft.segmentoOutros,
      });
    } else if (reviewEdit === "procura") {
      if (!editDraft.procura) {
        toast.error("Selecione o que você procura no fórum.");
        return;
      }
      if (
        editDraft.procura === "outros" &&
        !String(editDraft.outrosProcura || "").trim()
      ) {
        toast.error("Descreva o que procura no fórum.");
        return;
      }
      update({
        procura: editDraft.procura,
        outrosProcura: editDraft.outrosProcura,
      });
    } else if (reviewEdit === "faturamento") {
      if (!editDraft.faturamento) {
        toast.error("Selecione o faturamento.");
        return;
      }
      if (
        editDraft.segmento === "hospital" &&
        (editDraft.leitos === "" || Number(editDraft.leitos) < 0)
      ) {
        toast.error("Informe o número de leitos.");
        return;
      }
      if (
        editDraft.segmento === "operadora" &&
        (editDraft.vidas === "" || Number(editDraft.vidas) < 0)
      ) {
        toast.error("Informe o número de vidas.");
        return;
      }
      update({
        faturamento: editDraft.faturamento,
        leitos: editDraft.leitos,
        vidas: editDraft.vidas,
      });
    } else if (reviewEdit === "bio") {
      if (!String(editDraft.bioCurta || "").trim()) {
        toast.error("Informe sua mini bio ou mensagem para networking.");
        return;
      }
      update({ bioCurta: editDraft.bioCurta });
    } else if (reviewEdit === "atividade_interesse") {
      if (!editDraft.atividadeInteresse) {
        toast.error("Selecione a atividade de maior interesse.");
        return;
      }
      if (
        editDraft.atividadeInteresse === "outros" &&
        !String(editDraft.atividadeInteresseOutros || "").trim()
      ) {
        toast.error("Descreva o outro interesse.");
        return;
      }
      update({
        atividadeInteresse: editDraft.atividadeInteresse,
        atividadeInteresseOutros: editDraft.atividadeInteresseOutros,
      });
    } else if (reviewEdit === "uso_imagem") {
      update({ usoImagem: editDraft.usoImagem });
    } else if (reviewEdit) {
      update(editDraft);
    }
    setReviewEdit(null);
  }, [reviewEdit, modalBioFoto, editDraft, update]);

  useEffect(() => {
    if (reviewEdit !== "foto") {
      setModalFotoPreview("");
      return undefined;
    }
    if (!modalBioFoto) {
      setModalFotoPreview("");
      return undefined;
    }
    const u = URL.createObjectURL(modalBioFoto);
    setModalFotoPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [reviewEdit, modalBioFoto]);

  useEffect(() => {
    if (!reviewEdit) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setReviewEdit(null);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [reviewEdit]);

  const handleModalBioFotoChange = useCallback((e) => {
    const input = e.target;
    const f = input.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Envie apenas arquivo de imagem (JPG, PNG, WebP ou GIF).");
      input.value = "";
      return;
    }
    if (f.size > MAX_BIO_FOTO_BYTES) {
      toast.error("Imagem muito grande. Tamanho máximo: 5 MB.");
      input.value = "";
      return;
    }
    setModalBioFoto(f);
  }, []);

  const markTouched = (keys) => {
    setTouched((t) => {
      const n = { ...t };
      keys.forEach((k) => {
        n[k] = true;
      });
      return n;
    });
  };

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.getElementById("c26-main")?.scrollIntoView({
      block: "start",
      behavior: "auto",
    });
  }, [step, submitted]);

  const errorsStep0 = useMemo(() => {
    const e = {};
    const req = (k, cond, msg) => {
      if (cond) e[k] = msg;
    };
    req("nomeCracha", !form.nomeCracha.trim(), "Informe o nome para o crachá.");
    if (!isValidEmail(form.email)) {
      req("email", true, "E-mail inválido.");
    } else if (emailLoginJaExiste) {
      req("email", true, "Este e-mail já possui cadastro.");
    }
    const cpfD = onlyDigits(form.cpf);
    req("cpf", cpfD.length !== 11, "CPF deve ter 11 dígitos.");
    req(
      "dataNascimento",
      !isValidDateBr(form.dataNascimento),
      "Data de nascimento inválida (use DD/MM/AAAA)."
    );
    req("sexo", !form.sexo, "Selecione o sexo.");
    req(
      "telComercial",
      onlyDigits(form.telComercial).length < 10,
      "Telefone comercial inválido."
    );
    req("celular", onlyDigits(form.celular).length < 10, "Celular inválido.");
    req("estado", !form.estado, "Selecione o estado.");
    if (form.estado && !isRegiaoValidaParaEstado(form.estado, form.regiao)) {
      req("regiao", true, "Região inválida para o estado selecionado.");
    } else {
      req("regiao", !form.regiao, "Selecione o estado para carregar a região.");
    }
    req("nomeInstituicao", !form.nomeInstituicao.trim(), "Informe a instituição.");
    req("cargo", !form.cargo.trim(), "Informe o cargo.");
    req("segmento", !form.segmento, "Selecione o segmento.");
    if (form.segmento === "outros") {
      req(
        "segmentoOutros",
        !form.segmentoOutros.trim(),
        "Descreva o segmento."
      );
    }
    req("secretaria", !form.secretaria.trim(), "Informe o nome do(a) secretário(a).");
    req(
      "telSecretaria",
      onlyDigits(form.telSecretaria).length < 10,
      "Telefone do(a) secretário(a) inválido."
    );
    req(
      "emailSecretaria",
      !isValidEmail(form.emailSecretaria),
      "E-mail do(a) secretário(a) inválido."
    );
    return e;
  }, [form, emailLoginJaExiste]);

  const errorsStep1 = useMemo(() => {
    const e = {};
    if (!form.procura) e.procura = "Selecione o que você procura no fórum.";
    if (form.procura === "outros" && !form.outrosProcura.trim()) {
      e.outrosProcura = "Descreva.";
    }
    if (!form.faturamento) e.faturamento = "Selecione o faturamento.";
    if (form.segmento === "hospital" && !String(form.leitos).trim()) {
      e.leitos = "Informe o número de leitos.";
    }
    if (form.segmento === "operadora" && !String(form.vidas).trim()) {
      e.vidas = "Informe o número de vidas.";
    }
    if (!String(form.prioridade || "").trim()) {
      e.prioridade = "Informe a prioridade de investimento.";
    }
    if (!String(form.transformacao || "").trim()) {
      e.transformacao = "Informe o maior desafio da instituição.";
    }
    return e;
  }, [form]);

  const errorsStepBio = useMemo(() => {
    const e = {};
    if (!String(form.bioCurta || "").trim()) {
      e.bioCurta = "Informe sua mini bio ou mensagem para networking.";
    }
    if (!form.atividadeInteresse) {
      e.atividadeInteresse = "Selecione a atividade de maior interesse.";
    }
    if (
      form.atividadeInteresse === "outros" &&
      !String(form.atividadeInteresseOutros || "").trim()
    ) {
      e.atividadeInteresseOutros = "Descreva o outro interesse.";
    }
    if (!bioFoto) {
      e.bioFoto = "Envie uma foto para o perfil.";
    }
    if (!form.acaoEspecial) {
      e.acaoEspecial =
        "Selecione se tem interesse em participar da degustação de caviar.";
    }
    return e;
  }, [form, bioFoto]);

  const errorsStepReview = useMemo(() => {
    const e = {};
    if (!form.aceiteRegulamento) {
      e.aceiteRegulamento =
        "É necessário aceitar a declaração para concluir o credenciamento.";
    }
    return e;
  }, [form]);

  const handleNext = async () => {
    if (step === 0) {
      const step0Keys = [
        "nomeCracha",
        "email",
        "cpf",
        "dataNascimento",
        "sexo",
        "telComercial",
        "celular",
        "estado",
        "regiao",
        "nomeInstituicao",
        "cargo",
        "segmento",
        "secretaria",
        "telSecretaria",
        "emailSecretaria",
      ];
      if (form.segmento === "outros") step0Keys.push("segmentoOutros");

      if (Object.keys(errorsStep0).length > 0) {
        markTouched(step0Keys);
        toast.error("Preencha ou corrija os campos obrigatórios antes de avançar.");
        return;
      }

      if (isValidEmail(form.email)) {
        setEmailLoginVerificando(true);
        try {
          const existe = await usuarioJaCadastradoPorEmail(form.email);
          setEmailLoginJaExiste(existe);
          markTouched(["email"]);
          if (existe) {
            toast.error("Este e-mail já possui cadastro.");
            return;
          }
        } finally {
          setEmailLoginVerificando(false);
        }
      }

      setStep(1);
      return;
    }

    if (step === 1) {
      const step1Keys = [
        "procura",
        "faturamento",
        "prioridade",
        "transformacao",
        "sugestaoEmpresa",
      ];
      if (form.procura === "outros") step1Keys.push("outrosProcura");
      if (form.segmento === "hospital") step1Keys.push("leitos");
      if (form.segmento === "operadora") step1Keys.push("vidas");

      if (Object.keys(errorsStep1).length > 0) {
        markTouched(step1Keys);
        toast.error("Preencha ou corrija os campos obrigatórios antes de avançar.");
        return;
      }
      setStep(2);
      return;
    }

    if (step === 2) {
      const step2Keys = ["bioCurta", "atividadeInteresse", "bioFoto", "acaoEspecial"];
      if (form.atividadeInteresse === "outros") {
        step2Keys.push("atividadeInteresseOutros");
      }

      if (Object.keys(errorsStepBio).length > 0) {
        markTouched(step2Keys);
        toast.error(
          "Preencha a mini bio, selecione a atividade de interesse, envie a foto do perfil e responda sobre a ação especial antes de avançar."
        );
        return;
      }
      setStep(STEP_CONTEUDOS);
      return;
    }

    if (step === STEP_CONTEUDOS) {
      setStep(STEP_MATCH_MEET);
      return;
    }

    if (step === STEP_MATCH_MEET) {
      setStep(STEP_REVISAO);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    markTouched(["aceiteRegulamento"]);
    if (!form.aceiteRegulamento) {
      toast.error("Aceite a declaração para concluir o credenciamento.");
      return;
    }
    setSubmitting(true);
    try {
      const regulares = idsConteudosRegulares(selectedIds, catalogo);
      const empresaInteresse = empresasMatchMeet
        .map((e) => (typeof e === "string" ? e : e?.nome))
        .filter(Boolean)
        .join(";");
      const res = await submitCredenciamento2026(form, bioFoto, {
        conteudo1: regulares[0] ?? null,
        conteudo2: regulares[1] ?? null,
        empresaInteresse,
      });
      setProtocolo(res.protocolo);
      setSubmitted(true);
      toast.success(res.mensagem);
    } catch (err) {
      const msg =
        err?.response?.data?.mensagem ||
        err?.message ||
        "Não foi possível concluir. Tente novamente.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const showErr = (name, errMap) =>
    Boolean(touched[name] && errMap[name]);

  const stepper = (
    <ol className="c26-stepper" aria-label="Etapas do credenciamento">
      {STEPS_META.map((s, i) => {
        const active = !submitted && step === i;
        const done = submitted || step > i;
        const upcoming = !submitted && i > step;
        return (
          <li
            key={s.key}
            className={`c26-step${active ? " is-active" : ""}${done ? " is-done" : ""}${upcoming ? " is-upcoming" : ""}`}
            aria-current={active ? "step" : undefined}
          >
            <span className="c26-step-icon" aria-hidden="true">
              <s.Icon />
            </span>
            <span className="c26-step-label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );

  const field = (name, label, hint, input, errMap) => {
    const hintId = hint ? `${uid}-${name}-hint` : undefined;
    const errId = showErr(name, errMap) ? `${uid}-${name}-err` : undefined;
    const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
    return (
      <div className={`c26-field ${name === "bioCurta" ? "c26-field-full" : ""}`}>
        <div className="c26-label-block">
          <label className="c26-label" htmlFor={`${uid}-${name}`}>
            {label} <span className="req">*</span>
          </label>
        </div>
        {React.cloneElement(input, {
          id: `${uid}-${name}`,
          "aria-invalid": showErr(name, errMap),
          "aria-describedby": describedBy,
        })}
        {hint ? (
          <p className="c26-hint" id={hintId}>
            {hint}
          </p>
        ) : null}
        {showErr(name, errMap) ? (
          <span className="c26-error" id={errId} role="alert">
            {errMap[name]}
          </span>
        ) : null}
      </div>
    );
  };

  const panelStep0 = (
    <div className="c26-step-panel">
      <p className="c26-lead">
      Olá! Ficamos muito felizes e honrados com a sua confirmação de presença no Saúde Business Fórum 2026.<br />
      Preencha seus dados para agilizar o seu credenciamento.<br/>
      Dúvidas entre em contato através do e-mail:<a href="mailto:debora.g@informa.com">debora.g@informa.com</a>.
      </p>
      <div className="c26-form-grid c26-two">
        {field(
          "nomeCracha",
          "Nome para crachá",
          null,
          <input
            className="c26-input"
            value={form.nomeCracha}
            onChange={(e) => update({ nomeCracha: e.target.value })}
            onBlur={() => markTouched(["nomeCracha"])}
            autoComplete="name"
            required
          />,
          errorsStep0
        )}
        {field(
          "email",
          "E-mail (login)",
          <>
            Este será o seu e-mail de acesso.
            {emailLoginVerificando ? (
              <>
                {" "}
                <FaSpinner className="fa-spin" aria-hidden />
                <span className="visually-hidden"> Verificando e-mail…</span>
              </>
            ) : null}
          </>,
          <input
            type="email"
            className="c26-input"
            value={form.email}
            onChange={(e) => {
              setEmailLoginJaExiste(false);
              update({ email: e.target.value });
            }}
            onBlur={async (e) => {
              markTouched(["email"]);
              const v = e.target.value.trim();
              if (!isValidEmail(v)) {
                setEmailLoginJaExiste(false);
                return;
              }
              setEmailLoginVerificando(true);
              try {
                const existe = await usuarioJaCadastradoPorEmail(v);
                setEmailLoginJaExiste(existe);
              } finally {
                setEmailLoginVerificando(false);
              }
            }}
            autoComplete="email"
            required
          />,
          errorsStep0
        )}
        {field(
          "cpf",
          "CPF",
          null,
          <input
            className="c26-input"
            inputMode="numeric"
            value={form.cpf}
            onChange={(e) => update({ cpf: formatCpf(e.target.value) })}
            onBlur={() => markTouched(["cpf"])}
            required
          />,
          errorsStep0
        )}
        {field(
          "dataNascimento",
          "Data de nascimento",
          "Formato DD/MM/AAAA.",
          <input
            className="c26-input"
            placeholder="DD/MM/AAAA"
            value={form.dataNascimento}
            onChange={(e) => {
              const raw = onlyDigits(e.target.value).slice(0, 8);
              let out = raw;
              if (raw.length > 2) out = `${raw.slice(0, 2)}/${raw.slice(2)}`;
              if (raw.length > 4) out = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
              update({ dataNascimento: out });
            }}
            onBlur={() => markTouched(["dataNascimento"])}
            required
          />,
          errorsStep0
        )}
        <div className="c26-field c26-field-full">
          <span className="c26-label">
            Sexo <span className="req">*</span>
          </span>
          <div className="c26-radio-row" role="group" aria-label="Sexo">
            {["Masculino", "Feminino"].map((opt) => (
              <label key={opt} className="c26-radio">
                <input
                  type="radio"
                  name={`${uid}-sexo`}
                  checked={form.sexo === opt}
                  onChange={() => {
                    update({ sexo: opt });
                    markTouched(["sexo"]);
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
          {showErr("sexo", errorsStep0) ? (
            <span className="c26-error" role="alert">
              {errorsStep0.sexo}
            </span>
          ) : null}
        </div>
        {field(
          "telComercial",
          "Telefone comercial",
          null,
          <input
            className="c26-input"
            inputMode="tel"
            value={form.telComercial}
            onChange={(e) => update({ telComercial: formatPhone(e.target.value) })}
            onBlur={() => markTouched(["telComercial"])}
            autoComplete="tel"
            required
          />,
          errorsStep0
        )}
        {field(
          "celular",
          "Celular",
          "Usado para comunicações no dia do evento; informe um número que estará com você.",
          <input
            className="c26-input"
            inputMode="tel"
            value={form.celular}
            onChange={(e) => update({ celular: formatPhone(e.target.value) })}
            onBlur={() => markTouched(["celular"])}
            autoComplete="tel-national"
            required
          />,
          errorsStep0
        )}
        {field(
          "estado",
          "Estado",
          null,
          <select
            className="c26-select"
            value={form.estado}
            onChange={(e) => {
              const estado = e.target.value;
              update({ estado, regiao: regiaoPorEstado(estado) || "" });
            }}
            onBlur={() => markTouched(["estado", "regiao"])}
            required
          >
            <option value="">Selecione uma opção</option>
            {ESTADOS_BR.map((nome) => (
              <option key={nome} value={nome}>
                {nome}
              </option>
            ))}
          </select>,
          errorsStep0
        )}
        {field(
          "regiao",
          "Região",
          form.estado ? "Preenchida automaticamente conforme o estado." : "Selecione o estado primeiro.",
          <select
            className="c26-select"
            value={form.regiao}
            disabled={!form.estado}
            onChange={() => {}}
            onBlur={() => markTouched(["regiao"])}
            required
          >
            <option value="">
              {form.estado ? "Região" : "Selecione o estado primeiro"}
            </option>
            {REGIOES_BR.map((nome) => (
              <option key={nome} value={nome}>
                {nome}
              </option>
            ))}
          </select>,
          errorsStep0
        )}
        {field(
          "nomeInstituicao",
          "Nome da instituição",
          null,
          <input
            className="c26-input"
            value={form.nomeInstituicao}
            onChange={(e) => update({ nomeInstituicao: e.target.value })}
            onBlur={() => markTouched(["nomeInstituicao"])}
            required
          />,
          errorsStep0
        )}
        {field(
          "cargo",
          "Cargo",
          null,
          <input
            className="c26-input"
            value={form.cargo}
            onChange={(e) => update({ cargo: e.target.value })}
            onBlur={() => markTouched(["cargo"])}
            required
          />,
          errorsStep0
        )}
        <div className="c26-field c26-field-full">
          <label className="c26-label" htmlFor={`${uid}-segmento`}>
            Segmento <span className="req">*</span>
          </label>
          <select
            id={`${uid}-segmento`}
            className="c26-select"
            value={form.segmento}
            onChange={(e) => update({ segmento: e.target.value })}
            onBlur={() => markTouched(["segmento"])}
            aria-invalid={showErr("segmento", errorsStep0)}
          >
            <option value="">Selecione uma opção</option>
            {SEGMENTOS.map(([v, lab]) => (
              <option key={v} value={v}>
                {lab}
              </option>
            ))}
          </select>
          {showErr("segmento", errorsStep0) ? (
            <span className="c26-error" role="alert">
              {errorsStep0.segmento}
            </span>
          ) : null}
        </div>
        {form.segmento === "outros"
          ? field(
              "segmentoOutros",
              "Descrição do segmento",
              null,
              <input
                className="c26-input"
                value={form.segmentoOutros}
                onChange={(e) => update({ segmentoOutros: e.target.value })}
                onBlur={() => markTouched(["segmentoOutros"])}
                required
              />,
              errorsStep0
            )
          : null}
        {field(
          "secretaria",
          "Nome do(a) secretário(a)",
          null,
          <input
            className="c26-input"
            value={form.secretaria}
            onChange={(e) => update({ secretaria: e.target.value })}
            onBlur={() => markTouched(["secretaria"])}
            required
          />,
          errorsStep0
        )}
        {field(
          "telSecretaria",
          "Telefone do(a) secretário(a)",
          null,
          <input
            className="c26-input"
            inputMode="tel"
            value={form.telSecretaria}
            onChange={(e) => update({ telSecretaria: formatPhone(e.target.value) })}
            onBlur={() => markTouched(["telSecretaria"])}
            required
          />,
          errorsStep0
        )}
        {field(
          "emailSecretaria",
          "E-mail do(a) secretário(a)",
          null,
          <input
            type="email"
            className="c26-input"
            value={form.emailSecretaria}
            onChange={(e) => update({ emailSecretaria: e.target.value })}
            onBlur={() => markTouched(["emailSecretaria"])}
            required
          />,
          errorsStep0
        )}
      </div>
    </div>
  );

  const panelStep1 = (
    <div className="c26-step-panel">
      <p className="c26-lead">
        Informações institucionais e pesquisa do participante.
      </p>
      <div className="c26-form-grid">
        <div className="c26-field c26-field-full">
          <label className="c26-label" htmlFor={`${uid}-procura`}>
            O que você procura no Saúde Business Fórum? <span className="req">*</span>
          </label>
          <select
            id={`${uid}-procura`}
            className="c26-select"
            value={form.procura}
            onChange={(e) => update({ procura: e.target.value })}
            onBlur={() => markTouched(["procura"])}
            aria-invalid={showErr("procura", errorsStep1)}
          >
            <option value="">Selecione uma opção</option>
            {PROCURA.map(([v, lab]) => (
              <option key={v} value={v}>
                {lab}
              </option>
            ))}
          </select>
          {showErr("procura", errorsStep1) ? (
            <span className="c26-error" role="alert">
              {errorsStep1.procura}
            </span>
          ) : null}
        </div>
        {form.procura === "outros" ? (
          <div className="c26-field c26-field-full">
            <label className="c26-label" htmlFor={`${uid}-outrosProcura`}>
              Descreva <span className="req">*</span>
            </label>
            <input
              id={`${uid}-outrosProcura`}
              className="c26-input"
              value={form.outrosProcura}
              onChange={(e) => update({ outrosProcura: e.target.value })}
              onBlur={() => markTouched(["outrosProcura"])}
              aria-invalid={showErr("outrosProcura", errorsStep1)}
            />
            {showErr("outrosProcura", errorsStep1) ? (
              <span className="c26-error" role="alert">
                {errorsStep1.outrosProcura}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="c26-field c26-field-full">
          <label className="c26-label" htmlFor={`${uid}-faturamento`}>
            Faturamento da instituição em 2025 <span className="req">*</span>
          </label>
          <select
            id={`${uid}-faturamento`}
            className="c26-select"
            value={form.faturamento}
            onChange={(e) => update({ faturamento: e.target.value })}
            onBlur={() => markTouched(["faturamento"])}
            aria-invalid={showErr("faturamento", errorsStep1)}
          >
            <option value="">Selecione uma opção</option>
            {FATURAMENTO.map(([v, lab]) => (
              <option key={v} value={v}>
                {lab}
              </option>
            ))}
          </select>
          {showErr("faturamento", errorsStep1) ? (
            <span className="c26-error" role="alert">
              {errorsStep1.faturamento}
            </span>
          ) : null}
        </div>
        {form.segmento === "hospital" ? (
          <div className="c26-field c26-field-full">
            <label className="c26-label" htmlFor={`${uid}-leitos`}>
              Número de leitos <span className="req">*</span>
            </label>
            <input
              id={`${uid}-leitos`}
              type="number"
              min="0"
              className="c26-input"
              value={form.leitos}
              onChange={(e) => update({ leitos: e.target.value })}
              onBlur={() => markTouched(["leitos"])}
              aria-invalid={showErr("leitos", errorsStep1)}
            />
            {showErr("leitos", errorsStep1) ? (
              <span className="c26-error" role="alert">
                {errorsStep1.leitos}
              </span>
            ) : null}
          </div>
        ) : null}
        {form.segmento === "operadora" ? (
          <div className="c26-field c26-field-full">
            <label className="c26-label" htmlFor={`${uid}-vidas`}>
              Número de vidas <span className="req">*</span>
            </label>
            <input
              id={`${uid}-vidas`}
              type="number"
              min="0"
              className="c26-input"
              value={form.vidas}
              onChange={(e) => update({ vidas: e.target.value })}
              onBlur={() => markTouched(["vidas"])}
              aria-invalid={showErr("vidas", errorsStep1)}
            />
            {showErr("vidas", errorsStep1) ? (
              <span className="c26-error" role="alert">
                {errorsStep1.vidas}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="c26-field c26-field-full">
          <label className="c26-label" htmlFor={`${uid}-prioridade`}>
            Prioridade de investimento para 2026/2027 <span className="req">*</span>
          </label>
          <input
            id={`${uid}-prioridade`}
            className="c26-input"
            value={form.prioridade}
            onChange={(e) => update({ prioridade: e.target.value })}
            onBlur={() => markTouched(["prioridade"])}
            aria-invalid={showErr("prioridade", errorsStep1)}
            required
          />
          {showErr("prioridade", errorsStep1) ? (
            <span className="c26-error" role="alert">
              {errorsStep1.prioridade}
            </span>
          ) : null}
        </div>
        <div className="c26-field c26-field-full">
          <label className="c26-label" htmlFor={`${uid}-transformacao`}>
            Maior desafio da sua instituição hoje <span className="req">*</span>
          </label>
          <input
            id={`${uid}-transformacao`}
            className="c26-input"
            value={form.transformacao}
            onChange={(e) => update({ transformacao: e.target.value })}
            onBlur={() => markTouched(["transformacao"])}
            aria-invalid={showErr("transformacao", errorsStep1)}
            required
          />
          {showErr("transformacao", errorsStep1) ? (
            <span className="c26-error" role="alert">
              {errorsStep1.transformacao}
            </span>
          ) : null}
        </div>
        <div className="c26-field c26-field-full">
          <div className="c26-label-block">
            <label className="c26-label" htmlFor={`${uid}-sugestao-empresa`}>
              Qual empresa você gostaria de ver no Saúde Business Fórum 2027?
            </label>
          </div>
          <input
            id={`${uid}-sugestao-empresa`}
            className="c26-input"
            value={form.sugestaoEmpresa}
            onChange={(e) => update({ sugestaoEmpresa: e.target.value })}
            onBlur={() => markTouched(["sugestaoEmpresa"])}
            aria-invalid={showErr("sugestaoEmpresa", errorsStep1)}
            aria-describedby={`${uid}-sugestao-empresa-hint`}
          />
          <p className="c26-hint" id={`${uid}-sugestao-empresa-hint`}>
            Fique à vontade para indicar um segmento ou empresa.
          </p>
          {showErr("sugestaoEmpresa", errorsStep1) ? (
            <span className="c26-error" role="alert">
              {errorsStep1.sugestaoEmpresa}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  const panelStepBio = (
    <div className="c26-step-panel">
      <p className="c26-lead">
        Insira aqui uma mini-bio e adicione uma foto para o seu perfil
      </p>
      <div className="c26-form-grid">
        <div className="c26-field c26-field-full">
          <div className="c26-label-block">
            <label className="c26-label" htmlFor={`${uid}-bio`}>
              Mini bio ou mensagem para networking <span className="req">*</span>
            </label>
          </div>
          <textarea
            id={`${uid}-bio`}
            className="c26-textarea"
            rows={4}
            value={form.bioCurta}
            onChange={(e) => update({ bioCurta: e.target.value })}
            onBlur={() => markTouched(["bioCurta"])}
            placeholder="Uma ou duas linhas sobre você, interesses ou o que busca no evento."
            aria-invalid={showErr("bioCurta", errorsStepBio)}
            aria-describedby={
              showErr("bioCurta", errorsStepBio) ? `${uid}-bio-err` : undefined
            }
            required
          />
          {showErr("bioCurta", errorsStepBio) ? (
            <span className="c26-error" id={`${uid}-bio-err`} role="alert">
              {errorsStepBio.bioCurta}
            </span>
          ) : null}
        </div>
        <div className="c26-field c26-field-full">
          <label className="c26-label" htmlFor={`${uid}-atividade-interesse`}>
            Qual dessas atividades você tem mais interesse? <span className="req">*</span>
          </label>
          <select
            id={`${uid}-atividade-interesse`}
            className="c26-select"
            value={form.atividadeInteresse}
            onChange={(e) => update({ atividadeInteresse: e.target.value })}
            onBlur={() => markTouched(["atividadeInteresse"])}
            aria-invalid={showErr("atividadeInteresse", errorsStepBio)}
          >
            <option value="">Selecione uma opção</option>
            {ATIVIDADES_INTERESSE.map(([v, lab]) => (
              <option key={v} value={v}>
                {lab}
              </option>
            ))}
          </select>
          {showErr("atividadeInteresse", errorsStepBio) ? (
            <span className="c26-error" role="alert">
              {errorsStepBio.atividadeInteresse}
            </span>
          ) : null}
        </div>
        {form.atividadeInteresse === "outros" ? (
          <div className="c26-field c26-field-full">
            <label className="c26-label" htmlFor={`${uid}-atividade-interesse-outros`}>
              Qual é o outro interesse? <span className="req">*</span>
            </label>
            <input
              id={`${uid}-atividade-interesse-outros`}
              className="c26-input"
              value={form.atividadeInteresseOutros}
              onChange={(e) => update({ atividadeInteresseOutros: e.target.value })}
              onBlur={() => markTouched(["atividadeInteresseOutros"])}
              aria-invalid={showErr("atividadeInteresseOutros", errorsStepBio)}
            />
            {showErr("atividadeInteresseOutros", errorsStepBio) ? (
              <span className="c26-error" role="alert">
                {errorsStepBio.atividadeInteresseOutros}
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="c26-field c26-field-full">
          <div className="c26-label-block">
            <span className="c26-label" id={`${uid}-bio-foto-label`}>
              Foto para perfil <span className="req">*</span>
            </span>
          </div>
          <div
            className="c26-upload"
            role="group"
            aria-labelledby={`${uid}-bio-foto-label`}
            aria-invalid={showErr("bioFoto", errorsStepBio)}
          >
            <input
              ref={bioFileInputRef}
              type="file"
              id={`${uid}-bio-foto`}
              className="c26-upload-input"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleBioFotoChange}
              aria-describedby={
                showErr("bioFoto", errorsStepBio) ? `${uid}-bio-foto-err` : undefined
              }
            />
            {bioFotoPreview ? (
              <>
                <img
                  src={bioFotoPreview}
                  alt="Pré-visualização da foto selecionada"
                  className="c26-upload-preview"
                />
                <div className="c26-upload-actions">
                  <button
                    type="button"
                    className="c26-btn c26-btn-ghost c26-btn-small"
                    onClick={() => bioFileInputRef.current?.click()}
                  >
                    Trocar imagem
                  </button>
                  <button
                    type="button"
                    className="c26-btn c26-btn-ghost c26-btn-small"
                    onClick={clearBioFoto}
                  >
                    Remover
                  </button>
                </div>
              </>
            ) : (
              <label htmlFor={`${uid}-bio-foto`} className="c26-upload-zone">
                <strong>Toque ou clique para escolher uma imagem</strong>
                <p>Formatos: JPG, PNG, WebP ou GIF · tamanho máximo 5 MB</p>
              </label>
            )}
          </div>
          {showErr("bioFoto", errorsStepBio) ? (
            <span className="c26-error" id={`${uid}-bio-foto-err`} role="alert">
              {errorsStepBio.bioFoto}
            </span>
          ) : null}
        </div>
        <div className="c26-field c26-field-full c26-field-separated">
          <span className="c26-label" id={`${uid}-acao-especial-label`}>
            Durante a edição deste ano teremos uma ação de degustação de Caviar, você tem
            interesse em participar? <span className="req">*</span>
          </span>
          <div
            className="c26-radio-row"
            role="radiogroup"
            aria-labelledby={`${uid}-acao-especial-label`}
          >
            {[
              ["sim", "Sim"],
              ["nao", "Não"],
            ].map(([value, label]) => (
              <label key={value} className="c26-radio">
                <input
                  type="radio"
                  name={`${uid}-acao-especial`}
                  checked={form.acaoEspecial === value}
                  onChange={() => {
                    update({ acaoEspecial: value });
                    markTouched(["acaoEspecial"]);
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          {showErr("acaoEspecial", errorsStepBio) ? (
            <span className="c26-error" role="alert">
              {errorsStepBio.acaoEspecial}
            </span>
          ) : null}
        </div>
        <div className="c26-field c26-field-full">
          <div className="c26-termo">
            <p>
              As <strong>fotografias e imagens do evento</strong> poderão ser usadas pela{" "}
              <strong>Informa Markets</strong> para promover o setor e o evento. Você
              autoriza a utilização de sua <strong>imagem e/ou voz</strong> em materiais
              impressos ou virtuais, no Brasil e no exterior.
            </p>
          </div>
          <label className="c26-check" style={{ marginTop: "0.75rem" }}>
            <input
              type="checkbox"
              checked={form.usoImagem}
              onChange={(e) => update({ usoImagem: e.target.checked })}
            />
            <span>
              <strong>Concordo com a autorização de uso de imagem</strong> (opcional)
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  const reviewPencil = (key, ariaLabel) => (
    <button
      type="button"
      className="c26-review-edit-btn"
      onClick={() => openReviewEdit(key)}
      aria-label={ariaLabel}
    >
      <FaPen aria-hidden />
    </button>
  );

  const reviewList = (
    <dl className="c26-review">
      <dt>Crachá / e-mail</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.nomeCracha} — {form.email}
          </span>
          {reviewPencil("cracha_email", "Editar crachá e e-mail")}
        </div>
      </dd>
      <dt>Estado / Região</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.estado ? `${form.estado} — ${form.regiao}` : "—"}
          </span>
          {reviewPencil("estado_regiao", "Editar estado e região")}
        </div>
      </dd>
      <dt>Instituição</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.nomeInstituicao} — {form.cargo}
          </span>
          {reviewPencil("instituicao", "Editar instituição e cargo")}
        </div>
      </dd>
      <dt>Segmento</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {SEGMENTOS.find(([v]) => v === form.segmento)?.[1]}
            {form.segmento === "outros" ? ` (${form.segmentoOutros})` : ""}
          </span>
          {reviewPencil("segmento", "Editar segmento")}
        </div>
      </dd>
      <dt>Procura no fórum</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {PROCURA.find(([v]) => v === form.procura)?.[1]}
            {form.procura === "outros" ? ` — ${form.outrosProcura}` : ""}
          </span>
          {reviewPencil("procura", "Editar procura no fórum")}
        </div>
      </dd>
      <dt>Faturamento (2025)</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">{form.faturamento}</span>
          {reviewPencil("faturamento", "Editar faturamento")}
        </div>
      </dd>
      <dt>Mini bio</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.bioCurta.trim() ? form.bioCurta : "—"}
          </span>
          {reviewPencil("bio", "Editar mini bio")}
        </div>
      </dd>
      <dt>Atividade de interesse</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.atividadeInteresse
              ? form.atividadeInteresse === "outros"
                ? form.atividadeInteresseOutros.trim()
                  ? `Outros: ${form.atividadeInteresseOutros.trim()}`
                  : "Outros"
                : ATIVIDADES_INTERESSE.find(([v]) => v === form.atividadeInteresse)?.[1]
              : "—"}
          </span>
          {reviewPencil("atividade_interesse", "Editar atividade de interesse")}
        </div>
      </dd>
      <dt>Foto de perfil</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {bioFoto
              ? `${bioFoto.name} (${Math.round(bioFoto.size / 1024)} KB)`
              : "Nenhuma imagem enviada"}
          </span>
          {reviewPencil("foto", "Editar foto de perfil")}
        </div>
      </dd>
      <dt>Degustação de caviar</dt>
      <dd>
        <span className="c26-review-value">
          {form.acaoEspecial === "sim"
            ? "Sim"
            : form.acaoEspecial === "nao"
              ? "Não"
              : "—"}
        </span>
      </dd>
      <dt>Uso de imagem no evento</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {form.usoImagem ? "Autorizado" : "Não autorizado"}
          </span>
          {reviewPencil("uso_imagem", "Editar autorização de uso de imagem")}
        </div>
      </dd>
      <dt>Conteúdos</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {conteudosSelecionados.length === 0 ? (
              "Nenhum conteúdo selecionado"
            ) : (
              <ul className="c26-review-escolhas">
                {conteudosSelecionados.map((item, index) => (
                  <li key={item.id}>
                    <strong>Conteúdo {index + 1}:</strong> {item.nome}
                    <span className="c26-review-escolha-meta">
                      {" "}
                      — {formatarHorarioConteudo(item)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </span>
          <button
            type="button"
            className="c26-review-edit-btn"
            onClick={() => setStep(STEP_CONTEUDOS)}
            aria-label="Editar conteúdos"
          >
            <FaPen aria-hidden />
          </button>
        </div>
      </dd>
      <dt>Match &amp; Meet</dt>
      <dd>
        <div className="c26-review-dd-wrap">
          <span className="c26-review-value">
            {empresasMatchMeet.length === 0 ? (
              "Nenhuma empresa selecionada"
            ) : (
              <ul className="c26-review-escolhas">
                {empresasMatchMeet.map((empresa, index) => {
                  const nome =
                    typeof empresa === "string" ? empresa : empresa?.nome;
                  return (
                    <li key={empresa?.id ?? nome ?? index}>
                      <strong>Empresa {index + 1}:</strong> {nome}
                    </li>
                  );
                })}
              </ul>
            )}
          </span>
          <button
            type="button"
            className="c26-review-edit-btn"
            onClick={() => setStep(STEP_MATCH_MEET)}
            aria-label="Editar Match e Meet"
          >
            <FaPen aria-hidden />
          </button>
        </div>
      </dd>
    </dl>
  );

  const credentialPreview = (
    <article
      className="c26-credential"
      aria-label="Pré-visualização da credencial do evento"
    >
      <header className="c26-credential-header">
        <div className="c26-credential-header-top">
          <span className="c26-credential-brand">SBF</span>
          <span className="c26-credential-year">2026</span>
        </div>
        <p className="c26-credential-tagline">Saúde Business Fórum</p>
      </header>
      <div className="c26-credential-body">
        <div className="c26-credential-photo">
          {bioFotoPreview ? (
            <img
              src={bioFotoPreview}
              alt=""
              className="c26-credential-photo-img"
            />
          ) : (
            <div
              className="c26-credential-photo-placeholder"
              aria-hidden="true"
            >
              {initialsFromName(form.nomeCracha)}
            </div>
          )}
        </div>
        <p className="c26-credential-name">
          {form.nomeCracha.trim() || "Nome no crachá"}
        </p>
        <p className="c26-credential-job">
          {form.cargo.trim() || "Cargo"}
        </p>
        <p className="c26-credential-institution">
          {form.nomeInstituicao.trim() || "Instituição"}
        </p>
        <p className="c26-credential-email">{form.email.trim() || "—"}</p>
      </div>
      <footer className="c26-credential-footer c26-credential-footer--participante">
        <span className="c26-credential-badge-type">Participante</span>
        <span className="c26-credential-venue">Costa do Sauípe · Bahia</span>
      </footer>
    </article>
  );

  const panelStepConteudos = (
    <>
      {carregandoAgenda ? (
        <div className="c26-step-panel cc-page--center">
          <FaSpinner className="fa-spin" size={24} aria-hidden />
          <p>Carregando conteúdos…</p>
        </div>
      ) : (
        <PassoConteudos
          dias={dias}
          catalogo={catalogo}
          selectedIds={selectedIds}
          onChangeSelected={setSelectedIds}
        />
      )}
    </>
  );

  const panelStepMatchMeet = (
    <PassoMatchMeet
      empresas={empresasMatchMeet}
      onChangeEmpresas={setEmpresasMatchMeet}
    />
  );

  const panelStep3 = (
    <div className="c26-step-panel">
      <p className="c26-lead">
        Confira os dados antes de enviar.
      </p>
      <div className="c26-review-grid">
        <div className="c26-credential-column">
          <h2 className="c26-credential-preview-title">Credencial do evento</h2>
          {credentialPreview}
        </div>
        <div className="c26-review-summary">
          <h2 className="c26-review-heading">Resumo dos dados</h2>
          {reviewList}
        </div>
      </div>
      <div className="c26-field c26-field-full" style={{ marginTop: "1.25rem" }}>
        <label className="c26-check">
          <input
            type="checkbox"
            checked={form.aceiteRegulamento}
            onChange={(e) => {
              update({ aceiteRegulamento: e.target.checked });
              markTouched(["aceiteRegulamento"]);
            }}
            aria-invalid={showErr("aceiteRegulamento", errorsStepReview)}
          />
          <span>
            Declaro que as informações são verdadeiras e aceito seguir o regulamento do
            evento. <span className="req">*</span>
          </span>
        </label>
        {showErr("aceiteRegulamento", errorsStepReview) ? (
          <span className="c26-error" role="alert">
            {errorsStepReview.aceiteRegulamento}
          </span>
        ) : null}
      </div>
    </div>
  );

  const successBlock = (
    <div className="c26-success">
      <div className="c26-success-icon" aria-hidden="true">
        <FaCheck />
      </div>
      <h2 className="c26-title" style={{ marginBottom: "0.5rem" }}>
        Credenciamento recebido
      </h2>
      <p className="c26-lead" style={{ margin: "0 auto 1rem" }}>
      Obrigado por se credenciar para o Saúde Business Fórum 2026. Você receberá um e-mail com mais informações sobre o credenciamento.
      </p>
      <div className="c26-success-footer-banner" aria-hidden="true">
        <img
          src={footerCred2026}
          alt=""
          className="c26-success-footer-banner__img"
          decoding="async"
        />
      </div>
      {/* <div className="c26-actions" style={{ justifyContent: "center", border: "none" }}>
        <Link to="/login" className="c26-btn c26-btn-primary" style={{ textDecoration: "none" }}>
          Ir para o login
        </Link>
      </div> */}
    </div>
  );

  const reviewEditModal =
    reviewEdit != null ? (
      <div
        className="c26-modal-overlay"
        role="presentation"
        onClick={closeReviewEdit}
      >
        <div
          className="c26-modal-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${uid}-review-modal-title`}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id={`${uid}-review-modal-title`} className="c26-modal-title">
            {REVIEW_MODAL_TITLES[reviewEdit] ?? "Editar"}
          </h2>
          <div className="c26-modal-body">
            {reviewEdit === "cracha_email" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-nomeCracha`}>
                    Nome no crachá
                  </label>
                  <input
                    id={`${uid}-ed-nomeCracha`}
                    className="c26-input"
                    value={editDraft.nomeCracha}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, nomeCracha: e.target.value }))
                    }
                  />
                </div>
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-email`}>
                    E-mail
                    {emailModalVerificando ? (
                      <>
                        {" "}
                        <FaSpinner className="fa-spin" aria-hidden />
                        <span className="visually-hidden"> Verificando…</span>
                      </>
                    ) : null}
                  </label>
                  <input
                    id={`${uid}-ed-email`}
                    type="email"
                    className="c26-input"
                    value={editDraft.email}
                    aria-invalid={emailModalJaExiste}
                    onChange={(e) => {
                      setEmailModalJaExiste(false);
                      setEditDraft((d) => ({ ...d, email: e.target.value }));
                    }}
                    onBlur={async (e) => {
                      const v = e.target.value.trim();
                      if (!isValidEmail(v)) {
                        setEmailModalJaExiste(false);
                        return;
                      }
                      setEmailModalVerificando(true);
                      try {
                        const existe = await usuarioJaCadastradoPorEmail(v);
                        setEmailModalJaExiste(existe);
                      } finally {
                        setEmailModalVerificando(false);
                      }
                    }}
                  />
                  {emailModalJaExiste ? (
                    <span className="c26-error" role="alert">
                      Este e-mail já possui cadastro.
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
            {reviewEdit === "estado_regiao" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-estado`}>
                    Estado
                  </label>
                  <select
                    id={`${uid}-ed-estado`}
                    className="c26-select"
                    value={editDraft.estado}
                    onChange={(e) => {
                      const estado = e.target.value;
                      setEditDraft((d) => ({
                        ...d,
                        estado,
                        regiao: regiaoPorEstado(estado) || "",
                      }));
                    }}
                    required
                  >
                    {ESTADOS_BR.map((nome) => (
                      <option key={nome} value={nome}>
                        {nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-regiao`}>
                    Região
                  </label>
                  <select
                    id={`${uid}-ed-regiao`}
                    className="c26-select"
                    value={editDraft.regiao}
                    disabled
                    required
                  >
                    {REGIOES_BR.map((nome) => (
                      <option key={nome} value={nome}>
                        {nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}
            {reviewEdit === "instituicao" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-inst`}>
                    Instituição
                  </label>
                  <input
                    id={`${uid}-ed-inst`}
                    className="c26-input"
                    value={editDraft.nomeInstituicao}
                    onChange={(e) =>
                      setEditDraft((d) => ({
                        ...d,
                        nomeInstituicao: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-cargo`}>
                    Cargo
                  </label>
                  <input
                    id={`${uid}-ed-cargo`}
                    className="c26-input"
                    value={editDraft.cargo}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, cargo: e.target.value }))
                    }
                  />
                </div>
              </div>
            ) : null}
            {reviewEdit === "segmento" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-seg`}>
                    Segmento
                  </label>
                  <select
                    id={`${uid}-ed-seg`}
                    className="c26-select"
                    value={editDraft.segmento}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, segmento: e.target.value }))
                    }
                  >
                    {SEGMENTOS.map(([v, lab]) => (
                      <option key={v || "seg-e"} value={v}>
                        {lab}
                      </option>
                    ))}
                  </select>
                </div>
                {editDraft.segmento === "outros" ? (
                  <div className="c26-field c26-field-full">
                    <label className="c26-label" htmlFor={`${uid}-ed-seg-out`}>
                      Descreva o segmento
                    </label>
                    <input
                      id={`${uid}-ed-seg-out`}
                      className="c26-input"
                      value={editDraft.segmentoOutros}
                      onChange={(e) =>
                        setEditDraft((d) => ({
                          ...d,
                          segmentoOutros: e.target.value,
                        }))
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {reviewEdit === "procura" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-procura`}>
                    O que você procura no fórum
                  </label>
                  <select
                    id={`${uid}-ed-procura`}
                    className="c26-select"
                    value={editDraft.procura}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, procura: e.target.value }))
                    }
                  >
                    {PROCURA.map(([v, lab]) => (
                      <option key={v || "proc-e"} value={v}>
                        {lab}
                      </option>
                    ))}
                  </select>
                </div>
                {editDraft.procura === "outros" ? (
                  <div className="c26-field c26-field-full">
                    <label className="c26-label" htmlFor={`${uid}-ed-proc-out`}>
                      Descreva
                    </label>
                    <input
                      id={`${uid}-ed-proc-out`}
                      className="c26-input"
                      value={editDraft.outrosProcura}
                      onChange={(e) =>
                        setEditDraft((d) => ({
                          ...d,
                          outrosProcura: e.target.value,
                        }))
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {reviewEdit === "faturamento" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-fat`}>
                    Faturamento em 2024
                  </label>
                  <select
                    id={`${uid}-ed-fat`}
                    className="c26-select"
                    value={editDraft.faturamento}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, faturamento: e.target.value }))
                    }
                  >
                    {FATURAMENTO.map(([v, lab]) => (
                      <option key={v || "fat-e"} value={v}>
                        {lab}
                      </option>
                    ))}
                  </select>
                </div>
                {editDraft.segmento === "hospital" ? (
                  <div className="c26-field c26-field-full">
                    <label className="c26-label" htmlFor={`${uid}-ed-leitos`}>
                      Número de leitos
                    </label>
                    <input
                      id={`${uid}-ed-leitos`}
                      type="number"
                      min="0"
                      className="c26-input"
                      value={editDraft.leitos}
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, leitos: e.target.value }))
                      }
                    />
                  </div>
                ) : null}
                {editDraft.segmento === "operadora" ? (
                  <div className="c26-field c26-field-full">
                    <label className="c26-label" htmlFor={`${uid}-ed-vidas`}>
                      Número de vidas
                    </label>
                    <input
                      id={`${uid}-ed-vidas`}
                      type="number"
                      min="0"
                      className="c26-input"
                      value={editDraft.vidas}
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, vidas: e.target.value }))
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {reviewEdit === "bio" ? (
              <div className="c26-field c26-field-full">
                <label className="c26-label" htmlFor={`${uid}-ed-bio`}>
                  Mini bio
                </label>
                <textarea
                  id={`${uid}-ed-bio`}
                  className="c26-textarea"
                  rows={4}
                  value={editDraft.bioCurta}
                  onChange={(e) =>
                    setEditDraft((d) => ({ ...d, bioCurta: e.target.value }))
                  }
                />
              </div>
            ) : null}
            {reviewEdit === "atividade_interesse" ? (
              <div className="c26-form-grid">
                <div className="c26-field c26-field-full">
                  <label className="c26-label" htmlFor={`${uid}-ed-atividade-interesse`}>
                    Qual dessas atividades você tem mais interesse?
                  </label>
                  <select
                    id={`${uid}-ed-atividade-interesse`}
                    className="c26-select"
                    value={editDraft.atividadeInteresse}
                    onChange={(e) =>
                      setEditDraft((d) => ({
                        ...d,
                        atividadeInteresse: e.target.value,
                      }))
                    }
                  >
                    {ATIVIDADES_INTERESSE.map(([v, lab]) => (
                      <option key={v || "atividade-ed-empty"} value={v}>
                        {lab}
                      </option>
                    ))}
                  </select>
                </div>
                {editDraft.atividadeInteresse === "outros" ? (
                  <div className="c26-field c26-field-full">
                    <label className="c26-label" htmlFor={`${uid}-ed-atividade-outros`}>
                      Qual é o outro interesse?
                    </label>
                    <input
                      id={`${uid}-ed-atividade-outros`}
                      className="c26-input"
                      value={editDraft.atividadeInteresseOutros}
                      onChange={(e) =>
                        setEditDraft((d) => ({
                          ...d,
                          atividadeInteresseOutros: e.target.value,
                        }))
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {reviewEdit === "foto" ? (
              <div className="c26-upload">
                <input
                  ref={modalFotoInputRef}
                  type="file"
                  id={`${uid}-ed-foto`}
                  className="c26-upload-input"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleModalBioFotoChange}
                />
                {modalFotoPreview ? (
                  <>
                    <img
                      src={modalFotoPreview}
                      alt=""
                      className="c26-upload-preview"
                    />
                    <div className="c26-upload-actions">
                      <button
                        type="button"
                        className="c26-btn c26-btn-ghost c26-btn-small"
                        onClick={() => modalFotoInputRef.current?.click()}
                      >
                        Trocar imagem
                      </button>
                      <button
                        type="button"
                        className="c26-btn c26-btn-ghost c26-btn-small"
                        onClick={() => {
                          setModalBioFoto(null);
                          if (modalFotoInputRef.current)
                            modalFotoInputRef.current.value = "";
                        }}
                      >
                        Remover
                      </button>
                    </div>
                  </>
                ) : (
                  <label htmlFor={`${uid}-ed-foto`} className="c26-upload-zone">
                    <strong>Escolher imagem</strong>
                    <p>JPG, PNG, WebP ou GIF · máx. 5 MB</p>
                  </label>
                )}
              </div>
            ) : null}
            {reviewEdit === "uso_imagem" ? (
              <div className="c26-form-grid">
                <p className="c26-modal-termo-hint">
                  Imagens do evento podem ser usadas pela Informa Markets para divulgação,
                  conforme o regulamento do SBF 2026.
                </p>
                <label className="c26-check" style={{ marginTop: 0 }}>
                  <input
                    type="checkbox"
                    checked={editDraft.usoImagem}
                    onChange={(e) =>
                      setEditDraft((d) => ({ ...d, usoImagem: e.target.checked }))
                    }
                  />
                  <span>
                    <strong>Concordo com a autorização de uso de imagem</strong>
                  </span>
                </label>
              </div>
            ) : null}
          </div>
          <div className="c26-modal-actions">
            <button
              type="button"
              className="c26-btn c26-btn-ghost"
              onClick={closeReviewEdit}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="c26-btn c26-btn-primary"
              onClick={saveReviewEdit}
              disabled={emailModalVerificando}
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div className="c26-page">
      <a href="#c26-main" className="c26-skip-link">
        Ir para o formulário
      </a>
      <div className="c26-banner-wrap">
        <img
          src={bannerSbf}
          alt="Saúde Business Fórum 2026 — Costa do Sauípe, Bahia"
          className="c26-banner"
          width={1100}
          height={220}
          decoding="async"
        />
      </div>
      <div
        className={`c26-layout${submitted ? " c26-layout--success" : ""}`}
      >
        {!submitted ? (
          <aside className="c26-aside" aria-label="Identidade visual">
            <p className="c26-aside-badge">Credenciamento 2026 · Costa do Sauípe</p>
            <figure className="c26-aside-figure">
              <img src={ilustBaiana} alt="" decoding="async" />
              <figcaption className="visually-hidden">
                Ilustração decorativa de boas-vindas
              </figcaption>
            </figure>
          </aside>
        ) : null}
        <main className="c26-main" id="c26-main">
          {!submitted ? (
            <>
              <p className="c26-eyebrow">Participante</p>
              <h1 className="c26-title">Credenciamento SBF 2026</h1>
              {stepper}
              {step === 0 ? panelStep0 : null}
              {step === 1 ? panelStep1 : null}
              {step === 2 ? panelStepBio : null}
              {step === STEP_CONTEUDOS ? panelStepConteudos : null}
              {step === STEP_MATCH_MEET ? panelStepMatchMeet : null}
              {step === STEP_REVISAO ? panelStep3 : null}
              <div
                className={`c26-actions${step === 0 ? " c26-actions--first-step" : ""}`}
              >
                {step > 0 ? (
                  <button
                    type="button"
                    className="c26-btn c26-btn-ghost"
                    onClick={handleBack}
                    disabled={submitting}
                  >
                    Voltar
                  </button>
                ) : null}
                {step < STEP_REVISAO ? (
                  <button
                    type="button"
                    className="c26-btn c26-btn-primary"
                    onClick={handleNext}
                    disabled={
                      submitting ||
                      emailLoginVerificando ||
                      (step === STEP_CONTEUDOS && carregandoAgenda)
                    }
                  >
                    {step === STEP_CONTEUDOS
                      ? "Ir para Match & Meet"
                      : step === STEP_MATCH_MEET
                        ? "Revisar dados"
                        : "Avançar"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="c26-btn c26-btn-submit"
                    onClick={handleSubmit}
                    disabled={submitting || !form.aceiteRegulamento}
                    title={
                      !form.aceiteRegulamento
                        ? "Marque a declaração para concluir o credenciamento"
                        : undefined
                    }
                  >
                    {submitting ? (
                      <>
                        <FaSpinner className="fa-spin" aria-hidden />
                        Enviando…
                      </>
                    ) : (
                      "Concluir credenciamento"
                    )}
                  </button>
                )}
              </div>
            </>
          ) : (
            successBlock
          )}
          {/* <p className="c26-help">
            Rota nova: <code>/credenciamento-2026</code> — fluxo legado permanece em{" "}
            <Link to="/cadastro">/cadastro</Link>. Suporte:{" "}
            <a href="mailto:sbf@informa.com">sbf@informa.com</a>
          </p> */}
        </main>
      </div>
      {reviewEditModal}
    </div>
  );
}

export default Credenciamento2026;
